/**
 * Manufacturing cost calculator — INTERNAL USE ONLY.
 * Estimates what Do Your Shades pays the Chinese manufacturer (Qingyuan Leyou).
 *
 * See docs/manufacturing-cost-formula.md for the full spec.
 *
 * ─── Blind formula ───────────────────────────────────────────────────────────
 *   sqm = (widthIn × 2.54 / 100) × (heightIn × 2.54 / 100)
 *
 *   Manual/Cordless:
 *     sqm ≤ 1  →  $20.00 flat
 *     sqm > 1  →  21.82 × sqm + 2.88
 *
 *   Motorized:
 *     sqm ≤ 1  →  $78.00 flat
 *     sqm > 1  →  20.00 × sqm + 58.00
 *
 * ─── Remotes (per room, motorized only) ──────────────────────────────────────
 *   1–6 motorized blinds  →  1 × 6-ch remote  @ $8.90
 *   7+  motorized blinds  →  1 × 16-ch remote @ $12.50
 *
 * ─── Solar panels ─────────────────────────────────────────────────────────────
 *   $21.50 per panel
 *
 * ─── Shipping ─────────────────────────────────────────────────────────────────
 *   $9.85 per sqm (sum of all blinds' sqm).
 *   Estimate only — confirm final cost with Ida Blinds before quoting.
 */

// ─── Constants ────────────────────────────────────────────────────────────────

/** Flat rate for manual/cordless blinds where sqm ≤ 1 */
export const FLAT_MANUAL = 20.0;
/** Flat rate for motorized blinds where sqm ≤ 1 */
export const FLAT_MOTORIZED = 78.0;
/** Per-sqm rate for manual/cordless blinds where sqm > 1 */
export const RATE_MANUAL = 21.82;
/** Fixed add-on for manual/cordless blinds where sqm > 1 */
export const ADDON_MANUAL = 2.88;
/** Per-sqm rate for motorized blinds where sqm > 1 */
export const RATE_MOTORIZED = 20.0;
/** Base cost for motorized blinds where sqm > 1 */
export const BASE_MOTORIZED = 58.0;

/** 6-channel remote — used when a room has 1–6 motorized blinds */
export const REMOTE_6CH_COST = 8.9;
/** 16-channel remote — used when a room has 7+ motorized blinds */
export const REMOTE_16CH_COST = 12.5;

/** Cost per solar panel */
export const SOLAR_PANEL_COST = 21.5;
/** Estimated shipping cost per sqm across the whole order */
export const SHIPPING_RATE_PER_SQM = 9.85;
/** Flat tariff/express surcharge added on top of sqm-based shipping estimate */
export const SHIPPING_TARIFF_FLAT = 300.0;

// ─── Types ────────────────────────────────────────────────────────────────────

export type ControlType = "motorized" | "cordless" | "manual";

export interface BlindCostInput {
  widthIn: number;
  heightIn: number;
  /** Fabric/style code — kept for compatibility but no longer affects cost. */
  fabricCode: string;
  /** "motorized" uses motorized formula; "manual"/"cordless" use the same formula. */
  controlType?: ControlType;
  hasSolarPanel?: boolean;
}

export interface RoomCostInput {
  blinds: BlindCostInput[];
}

export interface OrderCostBreakdown {
  /** Cost of all blinds combined */
  blindsCost: number;
  /** Cost of all remotes combined */
  remotesCost: number;
  /** Number of 6-channel remotes needed across all rooms */
  remotes6ch: number;
  /** Number of 16-channel remotes needed across all rooms */
  remotes16ch: number;
  /** Cost of solar panels */
  solarCost: number;
  /** Number of solar panels */
  solarCount: number;
  /** Estimated shipping cost (sqm-based) */
  shippingCost: number;
  /** Total sqm across all blinds (used for shipping label) */
  totalSqm: number;
  /** Grand total manufacturing estimate */
  total: number;
  /** Per-blind cost breakdown (same order as input) */
  perBlind: number[];
}

// ─── Core helpers ─────────────────────────────────────────────────────────────

/**
 * Calculates the sqm for a blind (no tolerance deduction).
 */
export function calculateBlindSqm(widthIn: number, heightIn: number): number {
  return ((widthIn * 2.54) / 100) * ((heightIn * 2.54) / 100);
}

/**
 * Returns the estimated manufacturing cost (USD) for ONE blind.
 * Does NOT include remotes, solar, or shipping.
 */
export function calculateBlindCost(input: BlindCostInput): number {
  const { widthIn, heightIn, controlType = "motorized" } = input;

  const sqm = calculateBlindSqm(widthIn, heightIn);
  const isMotorized = controlType === "motorized";

  if (isMotorized) {
    return sqm <= 1 ? FLAT_MOTORIZED : RATE_MOTORIZED * sqm + BASE_MOTORIZED;
  } else {
    // manual or cordless — same formula
    return sqm <= 1 ? FLAT_MANUAL : RATE_MANUAL * sqm + ADDON_MANUAL;
  }
}

/**
 * Calculates the full manufacturing cost breakdown for a complete order.
 *
 * Remote logic (per room):
 *   - 1–6 motorized blinds → 1 × 6-channel remote @ $8.90
 *   - 7+  motorized blinds → 1 × 16-channel remote @ $12.50
 *
 * Shipping:
 *   - $9.85 × total sqm of all blinds in the order
 *
 * @param rooms  Array of rooms, each containing their blind inputs.
 */
export function calculateOrderCost(rooms: RoomCostInput[]): OrderCostBreakdown {
  let blindsCost = 0;
  let remotesCost = 0;
  let remotes6ch = 0;
  let remotes16ch = 0;
  let solarCost = 0;
  let solarCount = 0;
  let totalSqm = 0;
  const perBlind: number[] = [];

  for (const room of rooms) {
    const motorizedInRoom = room.blinds.filter(
      (b) => b.controlType === "motorized",
    ).length;

    if (motorizedInRoom > 0) {
      if (motorizedInRoom <= 6) {
        remotes6ch += 1;
        remotesCost += REMOTE_6CH_COST;
      } else {
        remotes16ch += 1;
        remotesCost += REMOTE_16CH_COST;
      }
    }

    for (const blind of room.blinds) {
      const cost = calculateBlindCost(blind);
      blindsCost += cost;
      perBlind.push(cost);

      totalSqm += calculateBlindSqm(blind.widthIn, blind.heightIn);

      if (blind.hasSolarPanel) {
        solarCount += 1;
        solarCost += SOLAR_PANEL_COST;
      }
    }
  }

  const shippingCost = SHIPPING_RATE_PER_SQM * totalSqm + SHIPPING_TARIFF_FLAT;
  const total = blindsCost + remotesCost + solarCost + shippingCost;

  return {
    blindsCost,
    remotesCost,
    remotes6ch,
    remotes16ch,
    solarCost,
    solarCount,
    shippingCost,
    totalSqm,
    total,
    perBlind,
  };
}

/**
 * Convenience wrapper — same as calculateBlindCost but with positional args.
 * Used by room-detail component for per-row display.
 */
export function calculateManufacturingCost(
  widthIn: number,
  heightIn: number,
  fabricCode: string,
  controlType: ControlType = "motorized",
): number {
  return calculateBlindCost({ widthIn, heightIn, fabricCode, controlType });
}
